import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import { api, trackWaClick } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { motion } from "framer-motion";
import { Crown, Zap, Star, Check, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    key: "Trial", tag: "COBA DULU", icon: Zap, name: "FAS Trial", duration: "10 Hari akses penuh", price: 69000, popular: false,
    features: [
      "Akses Briefing 08:15 WIB Pagi",
      "2 Rekomendasi Saham Harian",
      "3 Saham Scalping Volatil",
      "Semua Kalkulator Saham",
      "Analisis AI 950+ Saham",
      "Support Grup Telegram",
    ],
  },
  {
    key: "Active", tag: "PALING POPULER", icon: Star, name: "FAS Active", duration: "30 Hari akses penuh", price: 159000, popular: true,
    features: [
      "Semua fitur FAS Trial",
      "Fitur Probabilitas Top Gainer",
      "Alert Realtime Rekomendasi",
      "Watchlist + Alert Harga Multi-Target",
      "Grafik Detail Candle + RSI + MACD",
    ],
  },
  {
    key: "Pro", tag: "PALING HEMAT", icon: Crown, name: "FAS Pro", duration: "90 Hari akses penuh", price: 399000, popular: false,
    features: [
      "Semua fitur FAS Active",
      "Akses VIP Bandarmology Data",
      "Konsultasi Portofolio 1-on-1",
      "Akses Arsip Briefing & E-book",
      "Prioritas Eksekusi Sinyal",
      "Hemat s/d 40% per hari",
    ],
  },
];

export default function Trading() {
  const [products, setProducts] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get("/products?category=FAS%20TRADING").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const pilihPaket = (plan) => {
    const prod = products.find((p) => p.name.includes(plan.key));
    if (!prod) return toast.error("Paket belum tersedia, coba lagi sebentar");
    add(prod, 1);
    toast.success(`${plan.name} masuk keranjang`, { description: "Lanjut checkout untuk aktivasi membership" });
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-16">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-stone-950 text-sm font-bold shadow-md hover:bg-emerald-400 hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-8">
          <span className="tag-pill bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
            <Crown className="w-3 h-3" /> Bergabung Sekarang
          </span>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Paket Membership FAS Trading
          </h1>
          <p className="mt-4 text-stone-400 max-w-2xl text-sm md:text-base leading-relaxed">
            Buka seluruh sinyal, briefing pagi, dan fitur probabilitas VIP. Pilih durasi yang sesuai gaya trading Anda.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`trading-plan-${plan.key.toLowerCase()}`}
              className={`relative rounded-3xl p-7 border ${
                plan.popular
                  ? "bg-stone-900 border-emerald-500/60 shadow-xl shadow-emerald-500/10"
                  : "bg-stone-900/60 border-stone-800"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 tag-pill bg-emerald-400 text-stone-950">
                  Paling Populer
                </span>
              )}
              <div className="flex items-center gap-2 text-stone-400">
                <plan.icon className={`w-4 h-4 ${plan.popular ? "text-emerald-400" : ""}`} />
                <span className="text-[11px] font-mono uppercase tracking-[0.2em]">{plan.tag}</span>
              </div>
              <h3 className="mt-4 font-display font-black text-3xl">{plan.name}</h3>
              <p className="text-sm text-stone-400 mt-1">{plan.duration}</p>
              <div className="mt-4 font-display font-black text-4xl text-emerald-400">{rupiah(plan.price)}</div>
              <hr className="my-6 border-stone-800" />
              <ul className="space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-stone-300">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`trading-pilih-${plan.key.toLowerCase()}`}
                onClick={() => pilihPaket(plan)}
                className={`mt-8 w-full py-3.5 rounded-xl font-bold transition ${
                  plan.popular
                    ? "bg-emerald-400 text-stone-950 hover:bg-emerald-300"
                    : "bg-stone-800 text-white hover:bg-stone-700 border border-stone-700"
                }`}
              >
                Pilih Paket
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-stone-500">
          Membership diaktifkan admin setelah pembayaran via warung FASMART · Akses dikirim ke email/WA kamu
        </p>
      </section>

      <footer className="relative border-t border-stone-800 py-6 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("trading")} href="https://wa.me/6281377114000?text=Halo%20Admin%20FAS%20Group%2C%20saya%20mau%20tanya%20FAS%20Trading..." target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-500 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>FAS Trading by FAS Group · Belajar saham bareng mentor</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>
    </div>
  );
}
